import { Images } from '@/assets/Images'
import { formatRoleText } from '@/utils/helpers'
import { forwardRef } from 'react'

interface JDCardProps {
  name: string
  role: string
  userRole?: string
  notableQualities: string
  mainResponsibilities: string
  requirements: string
  isMyJD?: boolean
  showRibbon?: boolean
  hasContributors?: boolean
}

export const JDCard = forwardRef<HTMLDivElement, JDCardProps>(
  (
    {
      name,
      role,
      userRole,
      notableQualities,
      mainResponsibilities,
      requirements,
      showRibbon = true,
      hasContributors = false,
    },
    ref
  ) => {
    // Split text by bullet points or new lines
    const formatBulletPoints = (text: string) => {
      if (!text) return []
      // Split by newlines and filter empty
      const lines = text.split('\n').filter((line) => line.trim())
      return lines.map((line) => {
        // Remove leading dash or bullet if exists
        const trimmed = line.trim()
        return trimmed.startsWith('-') ? trimmed.substring(1).trim() : trimmed
      })
    }

    return (
      <div
        ref={ref}
        className="relative w-full rounded-md"
        style={{
          backgroundImage: `url(${Images.background_paper})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        {/* Ribbon */}
        {showRibbon && (
          <img
            src={Images.ribbon}
            alt="Ribbon"
            className="md:w-64 w-42 absolute top-0 right-0 z-10 h-auto"
            style={{ aspectRatio: '918 / 341' }}
          />
        )}

        {/* Top Section */}
        <div className="rounded-t-3xl md:pr-8 relative px-4 pr-4">
          {/* Two Column Layout */}
          <div className="md:grid md:grid-cols-2 gap-0">
            {/* Left: Header - With Background */}
            <div
              className=""
              style={{
                backgroundImage: `url(${Images.background_card})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top left',
                aspectRatio: '1223 / 2005',
              }}
            >
              <div className="space-y-2 pt-[40%] pl-[10%] pr-[20%] -rotate-4">
                <h1 className="font-momo-trust-display md:text-4xl text-primary text-3xl font-bold leading-tight">
                  Untold JD của
                  <br />
                  {name}
                </h1>
                <p className="md:text-xl text-lg font-medium">
                  {formatRoleText(role, userRole, true)}
                </p>
              </div>
            </div>

            {/* Right: Tổ chất nổi bật */}
            <div className="md:pt-[30%] px-2 pt-4">
              <div className="mb-4">
                <div className="border-primary border-b" />
                <h3 className="bg-primary rounded-br-3xl font-momo-trust-display inline-block px-4 py-1 text-xl font-bold text-white">
                  Tổ chất nổi bật
                </h3>
              </div>
              <ul className="space-y-2 text-base text-gray-800">
                {formatBulletPoints(notableQualities).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary flex-shrink-0 mt-1.5">✦</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="md:p-12 rounded-b-3xl p-8">
          {/* Divider */}
          <div className="border-primary border-t" />

          {/* Two Column Layout */}
          <div className="md:grid-cols-2 grid gap-6">
            {/* Left Column - Trách nhiệm chính */}
            <div>
              <div className="mb-4">
                <h3 className="bg-primary rounded-br-3xl font-momo-trust-display inline-block px-4 py-1 text-xl font-bold text-white">
                  Trách nhiệm chính
                </h3>
              </div>
              <ul className="space-y-2 text-base text-gray-800">
                {formatBulletPoints(mainResponsibilities).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary flex-shrink-0 mt-1.5">✦</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column - Yêu cầu chuyên môn riêng biệt */}
            <div>
              <div className="border-primary md:border-t-0 border-t" />
              <div className="mb-4">
                <h3 className="bg-primary rounded-br-3xl font-momo-trust-display inline-block px-4 py-1 text-xl font-bold text-white">
                  Yêu cầu chuyên môn riêng biệt
                </h3>
              </div>
              <ul className="space-y-2 text-base text-gray-800">
                {formatBulletPoints(requirements).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary flex-shrink-0 mt-1.5">✦</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contributors Message */}
          {hasContributors && (
            <div className="mt-8">
              <p className="text-primary text-sm">
                JD này được MoMo AI gói ghém lại từ những lời nhắn của MoMoers khắp làng trên xóm
                dưới gửi cho {name}, về những việc không tên mà mọi người muốn cảm ơn bạn, nhưng
                chưa có dịp nói ra.
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }
)

JDCard.displayName = 'JDCard'
